import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Switch } from "../../ui/switch";
import { useCookieConsent } from "./CookieConsentContext";
import type { ConsentCategories } from "../../../lib/cookieConsent";

const PRIMARY_BTN =
  "inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9] transition-transform hover:scale-[1.02] active:scale-[0.98] active:brightness-90";
const SECONDARY_BTN =
  "inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-[#0A0A0F] dark:text-white border border-[#0A0A0F]/15 dark:border-white/15 hover:border-[#1E4FD8] dark:hover:border-[#4A90D9] transition-colors";

const CATEGORY_COPY: { key: keyof ConsentCategories; label: string; description: string }[] = [
  { key: "functional", label: "Functional", description: "Remembers settings such as language or theme." },
  { key: "analytics", label: "Analytics", description: "Product analytics (PostHog) and error monitoring (Sentry) to measure usage and fix problems." },];

function CategoryRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-[#0A0A0F]/8 dark:border-white/8 last:border-b-0">
      <div>
        <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">{label}</p>
        <p className="text-sm text-[#5C5C66] dark:text-[#9A9A9A] mt-0.5">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} className="mt-1 shrink-0" />
    </div>
  );
}

export function CookiePreferenceCenter() {
  const { preferencesOpen, closePreferences, categories, acceptAll, rejectAll, saveCategories } = useCookieConsent();
  const [local, setLocal] = useState<ConsentCategories>(categories);

  useEffect(() => {
    if (preferencesOpen) setLocal(categories);
  }, [preferencesOpen, categories]);

  return (
    <Dialog open={preferencesOpen} onOpenChange={(open) => !open && closePreferences()}>
      <DialogContent className="bg-[#FAFAFA] dark:bg-[#0D0D10] border-[#0A0A0F]/10 dark:border-white/10 rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0A0A0F] dark:text-white">Cookie preferences</DialogTitle>
          <DialogDescription className="text-[#5C5C66] dark:text-[#9A9A9A]">
            Choose which categories to allow. Strictly necessary cookies are always on.
          </DialogDescription>
        </DialogHeader>

        <div>
          <CategoryRow
            label="Strictly necessary"
            description="Sign-in, session, security, load balancing. Cannot be turned off."
            checked
            disabled
          />
          {CATEGORY_COPY.map(({ key, label, description }) => (
            <CategoryRow
              key={key}
              label={label}
              description={description}
              checked={local[key]}
              onChange={(checked) => setLocal((prev) => ({ ...prev, [key]: checked }))}
            />
          ))}
        </div>

        <DialogFooter className="sm:justify-between">
          <div className="flex gap-3">
            <button onClick={rejectAll} className={SECONDARY_BTN}>Reject all</button>
            <button onClick={acceptAll} className={PRIMARY_BTN}>Accept all</button>
          </div>
          <button onClick={() => saveCategories(local)} className={PRIMARY_BTN}>Save choices</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
